/**
 * Canonical host registry — THE single source of truth for every hostname-
 * derived fact on the frontend.
 *
 * SRP: this file holds DATA (which hosts belong to which region, plus display
 * metadata). Parsing lives in `composables/useCurrentHost.ts`. Consumers read
 * derived values from the composable, never re-parse `window.location`.
 *
 * OCP: adding a new region = one entry in `CANONICAL_HOST_REGISTRY` below.
 * Every downstream — CANONICAL_HOSTS set, HOST_TO_REGION map, API host
 * derivation, region badge, region-scoped hero copy — picks it up.
 *
 * The `.local` hostnames are RFC 6762 reserved (local-network only) so they
 * can never collide with real customer domains; treating them as canonical
 * dev hosts is always safe.
 */

/** ISO 3166-1 alpha-2 region code — mirrors backend Zaveato.Domain.Common.RegionDefaults keys. */
export type RegionCode = 'GB' | 'PK' | 'AE' | 'SA'

export interface RegionHostEntry {
  /** Production apex + www + staging hosts belonging to this region. */
  readonly hosts: readonly string[]
  /** `.local` dev hostname used for `npm run dev`. */
  readonly devLocalHost: string
  /** Human-readable region name (used in badges + copy). */
  readonly label: string
  /** Unicode flag emoji (badge). */
  readonly flag: string
  /** Display code (may differ from RegionCode, e.g. GB region displays as "UK"). */
  readonly displayCode: string
  /** API host to talk to for this region on production/staging. `null` = fall back to VITE_API_URL. */
  readonly apiHostByHost: Readonly<Record<string, string>>
}

export const CANONICAL_HOST_REGISTRY: Readonly<Record<RegionCode, RegionHostEntry>> = {
  GB: {
    hosts: [
      'zaveato.co.uk',
      'www.zaveato.co.uk',
      'zaveato.com',
      'www.zaveato.com',
      'staging.zaveato.co.uk',
    ],
    devLocalHost: 'zaveato.local',
    label: 'United Kingdom',
    flag: '🇬🇧',
    displayCode: 'UK',
    apiHostByHost: {
      'zaveato.co.uk': 'api.zaveato.co.uk',
      'www.zaveato.co.uk': 'api.zaveato.co.uk',
      'zaveato.com': 'api.zaveato.co.uk',
      'www.zaveato.com': 'api.zaveato.co.uk',
      'staging.zaveato.co.uk': 'api-staging.zaveato.co.uk',
    },
  },
  PK: {
    hosts: [
      'zaveato.pk',
      'www.zaveato.pk',
      'pk-staging.zaveato.co.uk',
    ],
    devLocalHost: 'zaveato.pk.local',
    label: 'Pakistan',
    flag: '🇵🇰',
    displayCode: 'PK',
    apiHostByHost: {
      'zaveato.pk': 'api.zaveato.pk',
      'www.zaveato.pk': 'api.zaveato.pk',
      'pk-staging.zaveato.co.uk': 'api-pk-staging.zaveato.co.uk',
    },
  },
  AE: {
    hosts: ['zaveato.ae', 'www.zaveato.ae'],
    devLocalHost: 'zaveato.ae.local',
    label: 'United Arab Emirates',
    flag: '🇦🇪',
    displayCode: 'AE',
    apiHostByHost: {
      'zaveato.ae': 'api.zaveato.ae',
      'www.zaveato.ae': 'api.zaveato.ae',
    },
  },
  SA: {
    hosts: ['zaveato.sa', 'www.zaveato.sa'],
    devLocalHost: 'zaveato.sa.local',
    label: 'Saudi Arabia',
    flag: '🇸🇦',
    displayCode: 'SA',
    apiHostByHost: {
      'zaveato.sa': 'api.zaveato.sa',
      'www.zaveato.sa': 'api.zaveato.sa',
    },
  },
} as const

// ─── Derived views ──────────────────────────────────────────────────────
// All computed once at module load — DO NOT reproduce these lookups in
// consumer code. Import the derived structures below or read from the
// `useCurrentHost` composable.

const _regionEntries = Object.entries(CANONICAL_HOST_REGISTRY) as Array<[RegionCode, RegionHostEntry]>

/** Every canonical Zaveato host (all regions), plus loopback. `.local` is handled separately. */
export const CANONICAL_HOSTS: ReadonlySet<string> = new Set([
  'localhost',
  '127.0.0.1',
  ..._regionEntries.flatMap(([, r]) => [...r.hosts, r.devLocalHost]),
])

/** host → region code (canonical hosts only). */
export const HOST_TO_REGION: ReadonlyMap<string, RegionCode> = new Map(
  _regionEntries.flatMap(([code, r]) =>
    [...r.hosts, r.devLocalHost].map((h) => [h, code] as [string, RegionCode])
  )
)

/** browserHost → apiHost for production/staging derivation. Local dev + unknown hosts → not in map. */
export const HOST_TO_API_HOST: ReadonlyMap<string, string> = new Map(
  _regionEntries.flatMap(([, r]) => Object.entries(r.apiHostByHost))
)
